import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Heart, Trash2 } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const devErr = process.env.NODE_ENV !== 'production' ? console.error.bind(console) : () => {};

export default function Favorites() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [favIds, setFavIds] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/auth'); return; }
    fetchFavorites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading]);

  const fetchFavorites = async () => {
    try {
      const { data } = await axios.get(`${API}/favorites`, { withCredentials: true });
      setProducts(data);
      setFavIds(data.map(p => p.product_id));
    } catch (e) {
      // silently handle fetch error
      devErr('[Favorites] fetchFavorites failed', e);
    } finally {
      setLoading(false);
    }
  };

  const toggleFav = async (productId) => {
    if (favIds.includes(productId)) {
      await axios.delete(`${API}/favorites/${productId}`, { withCredentials: true });
      setProducts(prods => prods.filter(p => p.product_id !== productId));
      setFavIds(ids => ids.filter(id => id !== productId));
    } else {
      await axios.post(`${API}/favorites`, { product_id: productId }, { withCredentials: true });
      setFavIds(ids => [...ids, productId]);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white pt-20 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen pt-20" data-testid="favorites-page">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
        <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] font-semibold mb-3 font-work">Saved Items</p>
        <div className="flex items-end justify-between mb-12">
          <h1 className="font-playfair text-4xl text-[#0A0A0A]">My Favorites</h1>
          {products.length > 0 && (
            <p className="text-sm text-[#999999] font-work">{products.length} piece{products.length !== 1 ? 's' : ''} saved</p>
          )}
        </div>

        {products.length === 0 ? (
          <div className="text-center py-24">
            <Heart size={48} className="mx-auto text-[#E5E5E5] mb-6" />
            <p className="font-playfair text-2xl text-[#0A0A0A] mb-4">No saved items yet</p>
            <p className="text-[#999999] text-sm font-work mb-8">Browse our collection and save pieces you love</p>
            <Link to="/shop">
              <button className="bg-[#0A0A0A] text-white hover:bg-[#D4AF37] hover:text-[#0A0A0A] transition-colors px-10 py-4 text-xs uppercase tracking-widest font-work">
                Browse Collection
              </button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6 gap-y-12" data-testid="favorites-grid">
            {products.map(p => (
              <ProductCard
                key={p.product_id}
                product={p}
                isFav={favIds.includes(p.product_id)}
                onToggleFav={() => toggleFav(p.product_id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
